import { useState } from "react";
import { toast } from "sonner";
import { Edit, Trash2, PlusCircle, CheckCircle2, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useFetchSaccoGoals, useDeleteSaccoGoal, useFetchSaccoShares, useFetchSaccoWithdrawals, useFetchSaccoDeposits } from "@saccoHooks";
import { ContributionGoalForm } from "./ContributionGoalForm";
import {SaccoGoal} from "@/@types/sacco.models.ts";

interface ContributionGoalListProps {
  membershipId: string;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
}

function formatDate(dateStr: string) {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

interface GoalCardProps {
  goal: SaccoGoal;
  currentBalance: number;
  onEdit: (goal: SaccoGoal) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

function GoalCard({ goal, currentBalance, onEdit, onDelete, isDeleting }: GoalCardProps) {
  const target = Number(goal.target_amount);
  const progress = Math.min(100, target > 0 ? (currentBalance / target) * 100 : 0);
  const isAchieved = currentBalance >= target;

  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {isAchieved ? (
            <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
          ) : (
            <Target className="h-4 w-4 text-muted-foreground shrink-0" />
          )}
          <span className="font-medium truncate">{goal.goal_name}</span>
          {isAchieved && (
            <Badge variant="secondary" className="text-green-700 bg-green-100 shrink-0">
              Achieved
            </Badge>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(goal)}
            aria-label="Edit goal"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700"
            onClick={() => onDelete(goal.id)}
            disabled={isDeleting}
            aria-label="Delete goal"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">
            {goal.target_type === "deposits" ? "Deposits" : "Share Capital"}
          </span>
          <span className="font-medium">{progress.toFixed(1)}%</span>
        </div>
        <Progress value={progress} className="h-2" />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{formatCurrency(currentBalance)}</span>
          <span>Target: {formatCurrency(target)}</span>
        </div>
      </div>

      <div className="flex justify-between text-xs text-muted-foreground">
        <span>Due: {formatDate(goal.target_date)}</span>
        {goal.description && <span className="truncate ml-2">{goal.description}</span>}
      </div>
    </div>
  );
}

export function ContributionGoalList({ membershipId }: ContributionGoalListProps) {
  const { data: goals = [], isLoading } = useFetchSaccoGoals(membershipId);
  const deleteGoal = useDeleteSaccoGoal(membershipId);

  const { data: deposits = [] } = useFetchSaccoDeposits(membershipId);
  const { data: withdrawals = [] } = useFetchSaccoWithdrawals(membershipId);
  const { data: shares = [] } = useFetchSaccoShares(membershipId);

  // Compute balances directly — no loan repayments needed for goal progress
  const depositBalance = deposits.reduce((s, d) => s + Number(d.amount), 0)
    - withdrawals.reduce((s, w) => s + Number(w.amount), 0);
  const shareBalance = shares.reduce((s, sh) => s + Number(sh.units) * Number(sh.price_per_unit), 0);

  const [formOpen, setFormOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<SaccoGoal | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  function getCurrentBalance(goal: SaccoGoal) {
    return goal.target_type === "deposits" ? depositBalance : shareBalance;
  }

  function isGoalAchieved(goal: SaccoGoal) {
    return getCurrentBalance(goal) >= Number(goal.target_amount);
  }

  // Sort: unachieved by target_date asc, then achieved separately
  const unachieved = goals
    .filter((g) => !isGoalAchieved(g))
    .sort((a, b) => new Date(a.target_date).getTime() - new Date(b.target_date).getTime());

  const achieved = goals
    .filter((g) => isGoalAchieved(g))
    .sort((a, b) => new Date(a.target_date).getTime() - new Date(b.target_date).getTime());

  function handleEdit(goal: SaccoGoal) {
    setEditingGoal(goal);
    setFormOpen(true);
  }

  function handleAddNew() {
    setEditingGoal(undefined);
    setFormOpen(true);
  }

  function handleDeleteClick(id: string) {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  }

  async function handleConfirmDelete() {
    if (!deletingId) return;
    try {
      await deleteGoal.mutateAsync(deletingId);
      toast.success("Goal deleted successfully");
    } catch {
      toast.error("Failed to delete goal");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={handleAddNew} size="sm">
          <PlusCircle className="h-4 w-4 mr-2" />
          Add Goal
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground text-center py-4">Loading goals...</p>
      ) : goals.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          No contribution goals yet. Add your first goal above.
        </p>
      ) : (
        <div className="space-y-6">
          {unachieved.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground">Active Goals</h3>
              {unachieved.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  currentBalance={getCurrentBalance(goal)}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                  isDeleting={deleteGoal.isPending}
                />
              ))}
            </div>
          )}

          {achieved.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground">Achieved Goals</h3>
              {achieved.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  currentBalance={getCurrentBalance(goal)}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                  isDeleting={deleteGoal.isPending}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <ContributionGoalForm
        membershipId={membershipId}
        goal={editingGoal}
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditingGoal(undefined);
        }}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Goal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this contribution goal? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
